import { defineStore } from "pinia";
import { ToastFailed } from "@/helper/services/toast";
import http from "../helper/services/http";

import useAuthStore from "@/stores/auth";
import { useNotificationStore } from "./notification";
import { useUIStore } from "./ui";

import Folder from "../models/Folder";
import Files from "../models/Files";
import { convertTime } from "../helper/services/timeConverter";
import router from "../router";

export default defineStore("document", {
  state() {
    return {
      files: [],
      initialFiles: [],
      folders: [],
      initialFolders: [],
      selectedFolder: [],
      initialSelectedFolder: [],
      initialId: "99f3d0a5-e814-4141-a6a2-f5d882958d24",
      detailFile: {},
      histories: null,
      needSignFile: [],
      signedFiles: [],
      submissions: [],
      requestedSign: 0,
      requestSigned: 0,
      persetujuans: [],
      selectedSubmission: {},
      lastFetchTime: {},
      recycleBin: [],
    };
  },
  getters: {
    isDataExpired: (state) => (key) => {
      if (!state.lastFetchTime[key]) {
        return true;
      }
      const expirationTime = new Date(
        state.lastFetchTime[key].getTime() + 60 * 60 * 1000
      );
      return expirationTime < new Date();
    },
  },
  actions: {
    loadFolders({ search, id } = { search: "", id: "" }) {
      if (
        !this.isDataExpired("loadFolder") &&
        this.selectedFolder.length == this.initialSelectedFolder.length &&
        search == "" &&
        id == this.initialId
      ) {
        this.initialFolders.length > 0 && (this.folders = this.initialFolders);
        return;
      }
      useUIStore().setLoading(true);
      let params = { user_id: id || useAuthStore().user.id, recycle_bin: false };
      if (this.selectedFolder.length > 0)
        params = {
          folder_id: this.selectedFolder[this.selectedFolder.length - 1].id,
          recycle_bin: false
        };
      search && (params.search = search);

      http({ path: "docs" })
        .get("folders", { params })
        .then((res) => {
          if (params) {
            this.folders = [];
          }
          res.data.data
            .filter(folder => !folder.recycle_bin)
            .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
            .forEach((folder) => {
              if (!this.folders.some((exist) => folder.id == exist.id)) {
                this.folders.push(new Folder(folder));
              }
            });
          !search && (this.initialFolders = [...this.folders.filter(folder => !folder.recycle_bin)]);
          this.initialSelectedFolder = [...this.selectedFolder];
          this.lastFetchTime["loadFolder"] = new Date();
        })
        .catch((err) =>
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          })
        )
        .finally(() => useUIStore().setLoading(false));
    },
    async loadSignedFiles(id) {
      const params = id ? { file_id: id } : {};
      const response = await http({ path: "docs" }).get("file-signatures", {
        params,
      });
      return response.data.data;
    },
    loadFiles(
      { search, id, visibility } = { search: "", id: "", visibility: null }
    ) {
      if (
        !this.isDataExpired("loadFiles") &&
        this.selectedFolder.length == this.initialSelectedFolder.length &&
        search == "" &&
        id == this.initialId
      ) {
        this.initialFiles.length > 0 && (this.files = this.initialFiles);
        return;
      }
      useUIStore().setLoading(true);
      let params = { user_id: id || useAuthStore().user.id, visibility, recycle_bin: false };
      if (this.selectedFolder.length > 0)
        params = {
          folder_id: this.selectedFolder[this.selectedFolder.length - 1].id,
          recycle_bin: false
        };
      !id && (params.user_id = useAuthStore().user.id);

      search && (params.search = search);

      http({ path: "docs" })
        .get("files", { params })
        .then(async (res) => {
          if (params) {
            this.files = [];
          }

          res.data.data
            .filter(file => !file.recycle_bin)
            .forEach((file) => {
              if (!this.files.some((exist) => exist.id == file.id)) {
                this.files.push(new Files(file));
              }
            });
          !search && (this.initialFiles = [...this.files.filter(file => !file.recycle_bin)]);
          this.lastFetchTime["loadFiles"] = new Date();
        })
        .catch((err) =>
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          })
        )
        .finally(() => useUIStore().setLoading(false));
    },
    createFolder(folderName) {
      useUIStore().setLoading(true);
      let data = {
        name: folderName,
        user_id: useAuthStore().user.id,
      };
      if (this.selectedFolder.length > 0) {
        data.parent_id = this.selectedFolder[this.selectedFolder.length - 1].id;
      }
      http({ path: "docs" })
        .post("folders", data)
        .then((res) => {
          this.folders.unshift(new Folder(res.data.data));
        })
        .catch((err) =>
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          })
        )
        .finally(() => useUIStore().setLoading(false));
    },
    uploadFile(nameFile) {
      useUIStore().setLoading(true);
      let data = new FormData();

      for (let [key, value] of Object.entries(nameFile)) {
        data.append(`${key}`, value);
      }

      data.append("user_id", useAuthStore().user.id);
      data.append("user_name", useAuthStore().user.name);

      if (this.selectedFolder.length > 0) {
        data.append(
          "folder_id",
          this.selectedFolder[this.selectedFolder.length - 1].id
        );
      }

      http({ path: "docs" })
        .post("files", data)
        .then((res) => {
          this.files.unshift(new Files(res.data.data));
        })
        .catch((err) =>
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          })
        )
        .finally(() => useUIStore().setLoading(false));
    },
    renameFile(idFile, nameFile, isFile) {
      useUIStore().setLoading(true);
      let data = new FormData();
      data.append(`name`, nameFile);

      if (isFile) {
        http({ path: "docs" })
          .put(`files/${idFile}`, data)
          .then(() => {
            this.files.map((file) => {
              if (file.id == idFile) file.name = nameFile;
            });
          })
          .catch((err) =>
            ToastFailed.fire({
              icon: "error",
              title: err.message,
            })
          )
          .finally(() => useUIStore().setLoading(false));
      } else {
        http({ path: "docs" })
          .put(`folders/${idFile}`, {
            name: nameFile,
            user_id: useAuthStore().user.id,
          })
          .then(() => {
            let selectedFolder = this.folders.find(
              (folder) => folder.id == idFile
            );
            selectedFolder.rename(nameFile);
            this.initialFolders = [...this.folders];
          })
          .catch((err) =>
            ToastFailed.fire({
              icon: "error",
              title: err.message,
            })
          )
          .finally(() => useUIStore().setLoading(false));
      }
    },
    async placeSpeciment(
      { path, body, parameter } = { path: "add-initial", body: null, parameter }
    ) {
      return http({ path: "signer" })
        .post(path, Object.keys(body).length > 0 ? body : parameter, {
          responseType: "blob",
        })
        .then(async (res) => {
          if (res.status == 200) {
            useNotificationStore().createNotification({
              documentID: this.detailFile.id,
              content: `Document $1 berhasil ditandatangani secara ${
                path == "add-initial" ? "digital" : "elektronik"
              } oleh $user pada $time${convertTime(new Date())}$`,
              recaiver_id: this.detailFile.user_id,
            });
          }
          return res.data;
        })
        .catch((err) => {
          useNotificationStore().createNotification({
            documentID: this.detailFile.id,
            content: `Document $1 gagal ditandatangani secara ${
              path == "add-initial" ? "digital" : "elektronik"
            } oleh $user pada $time${convertTime(new Date())}$`,
          });
          throw err;
        });
    },
    async esignFile(files, bsre = 0, type) {
      useUIStore().setLoading(true);

      let data = new FormData();
      data.append("file_id", this.detailFile.id);
      data.append("user_id", useAuthStore().user.id);
      data.append("signature_name", useAuthStore().user.name);
      data.append("signed_file", files);
      data.append("bsre_signed", bsre);
      data.append("type_signed", type);

      return http({ path: "docs" })
        .post("file-signatures", data)
        .catch((err) => {
          throw err;
        })
        .finally(() => useUIStore().setLoading(false));
    },
    moveToRecycleBin({ type = "files", id }) {
      useUIStore().setLoading(true);
      const formData = new URLSearchParams();
      formData.append("recycle_bin", "true");
      formData.append("user_id", useAuthStore().user.id);
      
      http({ path: "docs" })
        .put(`${type}/${id}`, formData, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          }
        })
        .then(() => {
          this[type] = this[type].filter((item) => item.id !== id);
          if (type === "files") {
            this.initialFiles = this.initialFiles.filter((item) => item.id !== id);
          } else {
            this.initialFolders = this.initialFolders.filter((item) => item.id !== id);
          }
        })
        .catch((err) =>
          ToastFailed.fire({
            icon: "error",
            title: err.response.data.message,
          })
        )
        .finally(() => useUIStore().setLoading(false));
    },
    deleteItem({ type = "files", id }) {
      useUIStore().setLoading(true);
      http({ path: "docs" })
        .delete(`${type}/${id}`, { data: { user_id: useAuthStore().user.id } })
        .then(() => {
          this[type] = this[type].filter((item) => item.id !== id);
          // Also remove from recycleBin if it's there
          this.recycleBin = this.recycleBin.filter((item) => item.id !== id);
        })
        .catch((err) =>
          ToastFailed.fire({
            icon: "error",
            title: err.response.data.message,
          })
        )
        .finally(() => useUIStore().setLoading(false));
    },
    loadRecycleBin({ search } = { search: "" }) {
      useUIStore().setLoading(true);
      let params = { user_id: useAuthStore().user.id, recycle_bin: true };
      search && (params.search = search);

      http({ path: "docs" })
        .get("files", { params })
        .then(async (res) => {
          const recycleFiles = res.data.data
            .filter(file => file.recycle_bin)
            .map((file) => {
              const fileObj = new Files({ ...file, isFile: true });
              if (!fileObj.folderName) {
                fileObj.folderName = "Root";
              }
              return fileObj;
            });
          this.recycleBin = recycleFiles;
        })
        .catch((err) => {
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          });
          this.recycleBin = [];
        })
        .finally(() => useUIStore().setLoading(false));
    },
    restoreFromRecycleBin({ type = "files", id }) {
      useUIStore().setLoading(true);
      const formData = new URLSearchParams();
      formData.append("recycle_bin", "false");
      formData.append("user_id", useAuthStore().user.id);
      
      http({ path: "docs" })
        .put(`${type}/${id}`, formData, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          }
        })
        .then((res) => {
          this.recycleBin = this.recycleBin.filter((item) => item.id !== id);
          // Re-add to files/folders
          const restoredItem = res.data.data;
          if (type === "files") {
            this.files.unshift(new Files(restoredItem));
            this.initialFiles.unshift(new Files(restoredItem));
          } else {
            this.folders.unshift(new Folder(restoredItem));
            this.initialFolders.unshift(new Folder(restoredItem));
          }
        })
        .catch((err) =>
          ToastFailed.fire({
            icon: "error",
            title: err.response.data.message,
          })
        )
        .finally(() => useUIStore().setLoading(false));
    },
    async getFileDetail(id) {
      let signedArr = await this.loadSignedFiles(id);
      let selectedSigned = signedArr.find((signed) => signed.file_id == id);
      this.detailFile = await this.getDetailData(id);

      this.detailFile.signed_file = selectedSigned || null;
      this.persetujuans &&
        (this.detailFile.persetujuans = [...this.persetujuans]) &&
        (this.persetujuans = []);
    },
    removeDetailFile() {
      this.detailFile = {};
    },
    async getDetailData(id, type = "default", recycleBin = false) {
      useUIStore().setLoading(true);
      let path;
      let url;
      let params = {};
      switch (type) {
        case 'skck':
          path = `files-skck/${id}`;
          url =  'skck';
          break;
        default:
          path = `files/${id}`;
          url = 'docs';
          break;
      }
      if (recycleBin) {
        params.recycle_bin = "true";
      }
      return await http({ path: url })
        .get(path, { params })
        .then((res) => {
          return res.data.data;
        })
        .catch((err) => {
          let message = err.message;
          if (err.response.status == 404) {
            return;
          }
          ToastFailed.fire({
            icon: "error",
            title: message,
          });
        })
        .finally(() => useUIStore().setLoading(false));
    },
    submitComment(comment) {
      useUIStore().setLoading(true);
      let data = {
        file_id: this.detailFile.id,
        user_id: useAuthStore().user.id,
        comment,
      };
      http({ path: "docs" })
        .post("file-comments", data)
        .then((res) => {
          this.detailFile.file_comments.push(res.data.data);
        })
        .catch((err) =>
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          })
        )
        .finally(() => useUIStore().setLoading(false));
    },
    loadHistory(idFile) {
      useUIStore().setLoading(true);
      http({ path: "docs" })
        .get(`files/get-file-history/${idFile}`)
        .then(async (res) => {
          this.histories = res.data.data;
        })
        .catch((err) => {
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          });
        })
        .finally(() => useUIStore().setLoading(false));
    },
    requestSign(userArr, berjenjang) {
      useUIStore().setLoading(true);
      http({ path: "docs" })
        .post("file-signing-requests", {
          file_id: this.detailFile.id,
          arr_user_id: userArr,
          status: "submitted",
          berjenjang,
          sender_id: useAuthStore().user.id,
          tanggal: new Date(),
        })
        .then(async (res) => {
          const data = res.data.data;
          const requestList = await this.getSignRequest({
            file_id: this.detailFile.id,
            status: "submitted",
          });
          const { file_id, user_id, id } = requestList[0];

          await this.sendWhatsappNotification({ file_id, user_id, id });
          this.loadHistory(this.detailFile.id);
          this.detailFile.file_signing_requests?.push(data);
        })
        .catch((err) => {
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          });
        })
        .finally(() => useUIStore().setLoading(false));
    },
    undoRequestSign(file_id) {
      useUIStore().setLoading(true);
      http({ path: "docs" })
        .delete(`file-signing-requests/undo/${file_id}`)
        .then(() => {
          this.detailFile.file_signing_requests.length = 0;
        })
        .catch((err) => {
          ToastFailed.fire({
            icon: "error",
            title: err.response.data.message,
          });
        })
        .finally(() => useUIStore().setLoading(false));
    },
    async getNeedSignFile(
      { limit, offset, forced, search } = {
        limit: 20,
        offset: 0,
        forced: false,
        search: "",
      }
    ) {
      if (!this.isDataExpired("getNeedSignFile") && !forced) {
        return;
      }
      let params = { done: false, limit, offset };
      if (search) {
        params.search = search;
      }
      useUIStore().setLoading(true);
      let needSign = await this.getFileSigned(params); //"file-signing-requests"
      if (needSign.length < 1) {
        this.needSignFile = [];
        ToastFailed.fire({
          icon: "error",
          title: "Tidak ada data di halaman berikutnya.",
        });
        // this.needSignFile = [];
        return;
      }

      needSign = await Promise.all(
        needSign.map(async (item) => {
          const notFirst = item.persetujuans.some((arr) => arr.number != 1);

          if (
            item.berjenjang === "y" &&
            item.persetujuans.length > 0 &&
            notFirst
          ) {
            const fileBerjenjang = await this.getFileSigned({
              done: true,
              file_id: item.file_id,
            });
            const keyNumberArray = fileBerjenjang.find((obj) => {
              const persetujuans = obj.persetujuans;
              for (const persetujuan of persetujuans) {
                if (
                  persetujuan.number === item.persetujuans[0].number - 1 &&
                  item.persetujuans[0].created_at == persetujuan.created_at
                ) {
                  return obj;
                }
              }
            });
            if (keyNumberArray && keyNumberArray.status !== "submitted") {
              return item;
            }
          } else {
            return item;
          }
        })
      );

      this.needSignFile = needSign.filter((item) => item !== undefined);
      useUIStore().setLoading(false);
      this.lastFetchTime["getNeedSignFile"] = new Date();
    },
    async getSignedFile(
      { limit, forced, offset, search } = {
        limit: 20,
        forced: false,
        offset: 0,
        search: "",
      }
    ) {
      if (!this.isDataExpired("getSignedFile") && !forced) {
        return;
      }
      useUIStore().setLoading(true);
      let params = { done: true, limit, offset };
      if (search) {
        params.search = search;
      }
      let response = await this.getFileSigned(params);
      if (response.length > 0) {
        this.signedFiles = response;
        return true;
      } else {
        this.signedFiles = [];
        ToastFailed.fire({
          icon: "error",
          title: "Tidak ada data di halaman berikutnya.",
        });
      }
      useUIStore().setLoading(false);
      this.lastFetchTime["getSignedFile"] = new Date();
    },
    async getFileSigned({
      done = false,
      file_id = null,
      limit = 20,
      offset = 0,
      search = "",
    } = {}) {
      useUIStore().setLoading(true);
      let status = "submitted";
      let params = { user_id: useAuthStore().user.id, offset };

      done && (status = "accepted");
      file_id && (params.file_id = file_id) && delete params.user_id;
      limit && (params.limit = limit);
      search && (params.search = search);
      params.status = status;

      return await this.getSignRequest(params);
    },
    async getSignRequest(params) {
      return http({ path: "docs" })
        .get("file-signing-requests/Signrequest", {
          params,
        })
        .then(async (res) => {
          return res.data.data;
        })
        .catch((err) =>
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          })
        )
        .finally(() => useUIStore().setLoading(false));
    },
    sendWhatsappNotification(obj) {
      const { user_id, file_id, id } = obj;
      useUIStore().setLoading(true);
      return http({ path: "auth" })
        .post(`users/send-notification`, {
          id: user_id,
          file_id: file_id,
          signing_id: id,
        })
        .then(() => true)
        .catch((err) => {
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          });
        })
        .finally(() => useUIStore().setLoading(false));
    },
    async signFile(requestId) {
      useUIStore().setLoading(true);
      console.log(this.selectedSubmission);

      await http({ path: "docs" })
        .put(
          `file-signing-requests/${requestId}`,
          {
            file_id: this.detailFile.id,
            user_id: useAuthStore().user.id,
            status: "accepted",
            sender_id: this.selectedSubmission.sender_id,
            tanggal: new Date(),
          },
          {
            validateStatus: function (status) {
              return status < 500;
            },
          }
        )
        .then((res) => {
          this.needSignFile = this.needSignFile.filter(
            (file) => file.file_id != res.data.data.file_id
          );
        })
        .catch((err) =>
          ToastFailed.fire({ icon: "error", title: err.message })
        );

      http({ path: "docs" })
        .post(
          `file-signing-requests`,
          {
            file_id: this.detailFile.id,
            status: "accepted",
            berjenjang: "y",
            number: this.detailFile.persetujuans[0].number + 1,
            sender_id: this.selectedSubmission.sender_id,
            tanggal: new Date(),
          },
          {
            validateStatus: function (status) {
              return status < 500;
            },
          }
        )
        .then(async (res) => {
          if (res.status != 404) {
            const requestList = await this.getSignRequest({
              file_id: this.detailFile.id,
              status: "submitted",
            });
            const { file_id, user_id, id } = requestList[0];
            this.sendWhatsappNotification({ user_id, file_id, id });
            this.loadHistory(this.detailFile.id);
          }
        })
        .catch((err) => {
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          });
        })
        .finally(() => useUIStore().setLoading(false));
    },
    loadSignSubmission({ forced, search } = { forced: false, search: "" }) {
      console.log("tes");

      if (!this.isDataExpired("loadSignSubmission") && !forced) {
        return;
      }
      let params = { user_id: useAuthStore().user.id };
      if (search) {
        params.search = search;
      }
      useUIStore().setLoading(true);
      http({ path: "docs" })
        .get("file-signing-requests/StatusFileRequest", { params })
        .then(async (res) => {
          if (res.data.data.length > 0) {
            this.submissions = res.data.data
              .filter((data) => data.file_signing_request.length > 0)
              .map((data) => {
                const acceptedRequest = data.file_signing_request.filter(
                  (request) => request.status == "accepted"
                );

                let status;
                let progress;
                if (acceptedRequest.length < data.file_signing_request.length) {
                  status = `di Penerima ke - ${acceptedRequest.length + 1}`;
                  progress = acceptedRequest.length;
                } else if (
                  acceptedRequest.length == data.file_signing_request.length
                ) {
                  status = "selesai";
                  progress = "done";
                }

                return { ...data, status, progress };
              });

            this.lastFetchTime["loadSignSubmission"] = new Date();
          } else {
            this.submissions = [];
            this.lastFetchTime["loadSignSubmission"] = new Date();
            this.selectedSubmission = {};
            ToastFailed.fire({
              icon: "error",
              title: "Tidak ada data di halaman berikutnya.",
            });
          }
        })
        .catch((err) =>
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          })
        )
        .finally(() => useUIStore().setLoading(false));
    },
    loadDataSubmission() {
      if (!this.isDataExpired("loadDataSubmission")) {
        return;
      }
      useUIStore().setLoading(true);
      http({ path: "docs" })
        .get("file-signing-requests", {
          params: { user_id: useAuthStore().user.id },
        })
        .then(async (res) => {
          let data = res.data.data;

          this.requestSigned = data.filter(
            (data) => data.status == "accepted"
          ).length;
          this.requestedSign = data.filter(
            (data) => data.status != "accepted"
          ).length;
          this.lastFetchTime["loadDataSubmission"] = new Date();
        })
        .catch((err) =>
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          })
        )
        .finally(() => useUIStore().setLoading(false));
    },
    rejectSubmission(file_id, file_signing_requests_id) {
      useUIStore().setLoading(true);
      http({ path: "docs" })
        .post(`file-signing-requests/tolak-pengajuan`, {
          file_id,
          user_id: useAuthStore().user.id,
          user_name: useAuthStore().user.name,
          file_signing_requests_id,
        })
        .then(() => {
          this.loadHistory(this.detailFile.id);
          this.getNeedSignFile({ forced: true });
          router.push({ name: "home" });
        })
        .catch((err) => {
          console.error("eror");
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          });
        })
        .finally(() => useUIStore().setLoading(false));
    },
    cancelSubmission(id) {
      useUIStore().setLoading(true);
      http({ path: "docs" })
        .delete(`file-signing-requests/${id}`)
        .then(() => {
          this.submissions = this.submissions.filter((sub) => sub.id !== id);
        })
        .catch((err) =>
          ToastFailed.fire({
            icon: "error",
            title: err.message,
          })
        )
        .finally(() => useUIStore().setLoading(false));
    },
  },
});
